/* global WebSocket */
import Vue from 'vue'
import VueApollo from 'vue-apollo'
import { ApolloClient } from 'apollo-client'
import { ApolloLink, Observable } from 'apollo-link'
import { createHttpLink } from 'apollo-link-http'
import { onError } from 'apollo-link-error'
import { InMemoryCache } from 'apollo-cache-inmemory'
import { getMainDefinition } from 'apollo-utilities'
import { print } from 'graphql/language/printer'
import { graphQlSubscriptionUrl, graphQlUrl } from '@/constants/config'
import store from '@/store'

Vue.use(VueApollo)

const httpLink = createHttpLink({
  uri: graphQlUrl
  // fetchOptions: {
  //   credentials: 'include',
  // },
})

const middlewareLink = new ApolloLink((operation, forward) => {
  if (!localStorage.token) {
    localStorage.setItem('token', '')
  }

  operation.setContext({
    headers: {
      authorization: localStorage.getItem('token')
    }
  })
  return forward(operation)
})

const errorLink = onError(({graphQLErrors, networkError}) => {
  if (networkError) {
    console.log('[networkError', networkError) // eslint-disable-line no-console
  }

  if (graphQLErrors) {
    for (const err of graphQLErrors) {
      if (err.extensions.code === 'authentication_error') {
        store.dispatch('authenticationError')
      }
    }
  }
})

class SimpleWebSocketLink extends ApolloLink {
  constructor({uri, getConnectionParams}) {
    super()
    this.uri = uri
    this.getConnectionParams = getConnectionParams
    this.socket = null
    this.observers = {}
    this.operationId = 0
    this.socketReadyPromise = null
  }

  request(operation) {
    return new Observable(observer => {
      const operationId = this.generateOperationId()
      const startOperation = () => {
        this.observers[operationId] = observer
        this.sendMessage({
          id: operationId,
          type: 'start',
          payload: {
            query: print(operation.query),
            variables: operation.variables,
            operationName: operation.operationName
          }
        })
      }

      this.ensureSocket()
        .then(startOperation)
        .catch(error => observer.error(error))

      return () => {
        this.sendMessage({id: operationId, type: 'stop'})
        delete this.observers[operationId]
        this.closeSocketIfIdle()
      }
    })
  }

  ensureSocket() {
    if (this.socket && this.socket.readyState === WebSocket.OPEN) {
      return Promise.resolve()
    }

    if (this.socketReadyPromise) return this.socketReadyPromise

    this.socketReadyPromise = new Promise((resolve, reject) => {
      this.socket = new WebSocket(this.uri, 'graphql-ws')

      this.socket.onopen = () => {
        this.sendMessage({
          type: 'connection_init',
          payload: this.getConnectionParams ? this.getConnectionParams() : {}
        })
        resolve()
      }

      this.socket.onerror = error => {
        reject(error)
        this.handleAllObserversError(error)
      }

      this.socket.onclose = () => {
        this.socketReadyPromise = null
        this.handleAllObserversError(new Error('Subscription connection closed'))
      }

      this.socket.onmessage = event => this.handleMessage(event.data)
    })

    return this.socketReadyPromise
  }

  handleMessage(message) {
    try {
      const data = JSON.parse(message)
      if (data.type === 'ka' || data.type === 'connection_ack') return

      if (data.type === 'connection_error') {
        this.handleAllObserversError(new Error(data.payload ? JSON.stringify(data.payload) : 'Subscription connection error'))
        return
      }

      const observer = data.id ? this.observers[data.id] : null
      if (!observer) return

      if (data.type === 'data') {
        observer.next(data.payload)
      } else if (data.type === 'error') {
        observer.error(data.payload)
      } else if (data.type === 'complete') {
        observer.complete()
        delete this.observers[data.id]
        this.closeSocketIfIdle()
      }
    } catch (e) {
      this.handleAllObserversError(e)
    }
  }

  sendMessage(message) {
    if (this.socket && this.socket.readyState === WebSocket.OPEN) {
      this.socket.send(JSON.stringify(message))
    }
  }

  handleAllObserversError(error) {
    Object.keys(this.observers).forEach(key => {
      const observer = this.observers[key]
      if (observer) {
        observer.error(error)
      }
    })
    this.observers = {}
    this.closeSocket()
  }

  closeSocketIfIdle() {
    if (Object.keys(this.observers).length === 0) {
      this.closeSocket()
    }
  }

  closeSocket() {
    if (this.socket) {
      if (this.socket.readyState === WebSocket.OPEN) {
        this.sendMessage({type: 'connection_terminate'})
      }
      this.socket.close()
      this.socket = null
      this.socketReadyPromise = null
    }
  }

  generateOperationId() {
    this.operationId += 1
    return String(this.operationId)
  }
}

const getSubscriptionLink = () => {
  if (!graphQlSubscriptionUrl) return null

  return new SimpleWebSocketLink({
    uri: graphQlSubscriptionUrl,
    getConnectionParams: () => ({
      authorization: localStorage.getItem('token') || ''
    })
  })
}

const subscriptionLink = getSubscriptionLink()

const splitLink = subscriptionLink
  ? ApolloLink.split(
    ({query}) => {
      const definition = getMainDefinition(query)
      return definition.kind === 'OperationDefinition' && definition.operation === 'subscription'
    },
    subscriptionLink,
    middlewareLink.concat(httpLink)
  )
  : middlewareLink.concat(httpLink)

const link = errorLink.concat(splitLink)

// Cache implementation
const cache = new InMemoryCache({
  addTypename: false
})

// Create the apollo client
export const apolloClient = new ApolloClient({
  link,
  cache
})

export const apolloProvider = new VueApollo({
  defaultClient: apolloClient
})
